import type { Json } from "@/integrations/supabase/types";
import type { BriefingRow } from "./supabase-helpers";
import type {
  BriefingStage,
  BriefingCustomSubject,
  CameraSettings,
  Restrictions,
  CateringSettings,
  CateringMealConfig,
  BriefingWithTypedJson,
} from "./briefing";

// Internal helper: narrows Json to unknown after a runtime guard,
// so each parser can cast to its target type in one place.
function narrow(json: Json): unknown {
  return json;
}

/**
 * Parse a Json value to a target type after a runtime object guard.
 * Use for one-off Json → T conversions where a dedicated parser doesn't exist.
 */
export function parseJsonObject<T>(json: Json | null | undefined, fallback: T): T {
  if (!json || typeof json !== "object" || Array.isArray(json)) return fallback;
  return narrow(json) as T;
}

/**
 * Cast a briefing row's Json columns to their strong types.
 * Use this once at the query boundary so downstream code gets real types.
 */
export function typedBriefing(row: BriefingRow): BriefingWithTypedJson {
  return row as BriefingWithTypedJson;
}

export function parseStages(json: Json | null): BriefingStage[] {
  if (!json || !Array.isArray(json)) return [];
  return narrow(json) as BriefingStage[];
}

export function parseCustomSubjects(json: Json | null): BriefingCustomSubject[] {
  if (!json || !Array.isArray(json)) return [];
  return narrow(json) as BriefingCustomSubject[];
}

export function parseCameraSettings(json: Json | null): CameraSettings | null {
  if (!json || typeof json !== "object" || Array.isArray(json)) return null;
  return narrow(json) as CameraSettings;
}

export function parseRestrictions(json: Json | null): Restrictions | null {
  if (!json || typeof json !== "object" || Array.isArray(json)) return null;
  return narrow(json) as Restrictions;
}

export function parseCatering(json: Json | null): CateringSettings | null {
  if (!json || typeof json !== "object" || Array.isArray(json)) return null;
  return narrow(json) as CateringSettings;
}

export function parseCateringEntries(json: Json | null): [string, CateringMealConfig][] {
  if (!json || typeof json !== "object" || Array.isArray(json)) return [];
  return Object.entries(narrow(json) as Record<string, CateringMealConfig>);
}

// GeoJSON polygon of festival_areas.polygon: one reader for web, crew and the
// producer app, in @produce/shared.
export { parsePolygon, type GeoJsonPolygon } from "@produce/shared";

export function parseGeoFeature(json: Json | null): GeoJSON.Feature | null {
  if (!json || typeof json !== "object" || Array.isArray(json)) return null;
  return narrow(json) as GeoJSON.Feature;
}

/**
 * Convert a GeoJSON geometry to Json for Supabase storage.
 */
export function geometryToJson(geometry: GeoJSON.Geometry): Json {
  return JSON.parse(JSON.stringify(geometry)) as Json;
}

/**
 * Convert a GeoJSON feature to Json for Supabase storage.
 */
export function featureToJson(feature: GeoJSON.Feature): Json {
  return JSON.parse(JSON.stringify(feature)) as Json;
}
